import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateRoomDisplayPosterDTO,
  CreateRoomDisplayScreenDTO,
  UpdateRoomDisplayPosterDTO,
  UpdateRoomDisplayScreenDTO,
} from "@/modules/room-displays/domain/contracts/room-displays";
import { publicRoomDisplaysApi, roomDisplaysApi } from "./client";

const keys = {
  all: ["room-displays"] as const,
  list: () => [...keys.all, "list"] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
  public: (slug: string) => [...keys.all, "public", slug] as const,
};

export function useRoomDisplaysQuery() {
  return useQuery({
    queryKey: keys.list(),
    queryFn: () => roomDisplaysApi.list(),
  });
}

export function useRoomDisplayQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => roomDisplaysApi.getById(id),
    enabled: !!id,
  });
}

export function usePublicRoomDisplayQuery(slug: string) {
  return useQuery({
    queryKey: keys.public(slug),
    queryFn: () => publicRoomDisplaysApi.getBySlug(slug),
    enabled: !!slug,
    refetchInterval: 45_000,
    retry: 1,
  });
}

export function useCreateRoomDisplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoomDisplayScreenDTO) =>
      roomDisplaysApi.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useUpdateRoomDisplay(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRoomDisplayScreenDTO) =>
      roomDisplaysApi.update(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
      void qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useDeleteRoomDisplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roomDisplaysApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useCreatePoster(screenId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoomDisplayPosterDTO) =>
      roomDisplaysApi.createPoster(screenId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(screenId) });
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useUpdatePoster(screenId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      posterId,
      input,
    }: {
      posterId: string;
      input: UpdateRoomDisplayPosterDTO;
    }) => roomDisplaysApi.updatePoster(screenId, posterId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(screenId) });
    },
  });
}

export function useDeletePoster(screenId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (posterId: string) =>
      roomDisplaysApi.deletePoster(screenId, posterId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(screenId) });
      void qc.invalidateQueries({ queryKey: keys.list() });
    },
  });
}

export function useReorderPosters(screenId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (posterIds: string[]) =>
      roomDisplaysApi.reorderPosters(screenId, posterIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.detail(screenId) });
    },
  });
}
